import { downloadMediaMessage } from "@whiskeysockets/baileys";
import fs from "fs/promises";
import path from "path";
import { fileURLToPath } from "url";
import { dirname } from "path";
import { randomUUID } from "crypto";
import { downloadAndSaveMedia as sharedDownloadAndSaveMedia, ensureMediaDir } from "./mediaDownloader.js";

// ESM __dirname polyfill
const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);

// Media storage configuration - Save to Laravel's public directory
const MEDIA_DIR = path.join(__dirname, "../../public/storage/media");
const MEDIA_URL_BASE =
    process.env.MEDIA_URL_BASE ||
    process.env.LARAVEL_APP_URL ||
    "https://scan.flowkirim.com";

// Media size limit configuration (5MB default) - kept for webhook payload compatibility
const MAX_MEDIA_SIZE_BYTES =
    parseInt(process.env.MAX_MEDIA_SIZE_MB || "5") * 1024 * 1024;

// Helper to get file extension from mimetype
const getExtensionFromMime = (mimetype) => {
    const mimeMap = {
        "image/jpeg": ".jpg",
        "image/png": ".png",
        "image/webp": ".webp",
        "image/gif": ".gif",
        "video/mp4": ".mp4",
        "video/webm": ".webm",
        "audio/mpeg": ".mp3",
        "audio/mp4": ".m4a",
        "audio/ogg": ".ogg",
        "audio/amr": ".amr",
        "application/pdf": ".pdf",
        "application/msword": ".doc",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            ".docx",
        "application/vnd.ms-excel": ".xls",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            ".xlsx",
        "application/vnd.ms-powerpoint": ".ppt",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation":
            ".pptx",
        "text/plain": ".txt",
        "application/webp": ".webp",
    };
    return mimeMap[mimetype] || ".bin";
};

// Wrapper for shared downloadAndSaveMedia - returns compatible format for webhook payload
// Uses shared mediaDownloader.js to avoid duplicate downloads
const downloadAndSaveMedia = async (message) => {
    const result = await sharedDownloadAndSaveMedia(message);

    if (!result) {
        return {
            filename: null,
            filepath: null,
            accessibleUrl: null,
            size: null,
            mimetype: null,
            error: 'Failed to download media: unknown error',
        };
    }

    return {
        filename: result.filename,
        filepath: result.filename ? path.join(MEDIA_DIR, result.filename) : null,
        accessibleUrl: result.accessibleUrl,
        size: result.size,
        mimetype: result.mimetype,
        error: result.error || null,
        fileSizeExceeded: result.fileSizeExceeded || false,
        timeout: result.timeout || false,
        serverBusy: result.serverBusy || false,
    };
};

// Format file size for human readability
const formatFileSize = (bytes) => {
    if (!bytes) return null;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
};

// Helper function to safely convert timestamp to ISO string
const safeTimestampToISO = (timestamp) => {
    try {
        // Handle null/undefined
        if (timestamp === null || timestamp === undefined) {
            console.debug('Timestamp is null/undefined, using current time');
            return new Date().toISOString();
        }

        // Log original timestamp for debugging
        console.debug('Converting timestamp:', timestamp, 'type:', typeof timestamp);

        // Convert to number if string
        let ts = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp;

        // Validate it's a valid number
        if (isNaN(ts) || !isFinite(ts)) {
            console.warn('Invalid timestamp (NaN/Infinite), using current time:', timestamp);
            return new Date().toISOString();
        }

        // WhatsApp timestamps can be in seconds OR milliseconds
        // If timestamp is before year 2000, it's likely in seconds
        // Year 2000 = 946684800000 ms or 946684800 s
        const MIN_TIMESTAMP_MS = 946684800000; // Jan 1, 2000 in milliseconds

        let date;
        if (ts < MIN_TIMESTAMP_MS) {
            // Timestamp is in seconds, convert to milliseconds
            date = new Date(ts * 1000);
            console.debug('Timestamp was in seconds, converted:', { original: ts, converted: ts * 1000, date: date.toISOString() });
        } else {
            // Timestamp is already in milliseconds
            date = new Date(ts);
            console.debug('Timestamp was in milliseconds:', { ts, date: date.toISOString() });
        }

        // Validate the resulting date
        if (isNaN(date.getTime())) {
            console.warn('Invalid date created from timestamp, using current time:', timestamp);
            return new Date().toISOString();
        }

        // Check if date is reasonable (not too far in future or past)
        const now = Date.now();
        const oneYearMs = 365 * 24 * 60 * 60 * 1000;
        if (date.getTime() > now + oneYearMs || date.getTime() < now - (10 * oneYearMs)) {
            console.warn('Timestamp out of reasonable range, using current time:', {
                timestamp,
                date: date.toISOString(),
                now: new Date().toISOString()
            });
            return new Date().toISOString();
        }

        console.debug('✅ Timestamp conversion successful:', date.toISOString());
        return date.toISOString();
    } catch (error) {
        console.error('❌ Error converting timestamp to ISO:', error, 'timestamp:', timestamp);
        return new Date().toISOString();
    }
};

// Format duration for audio/video
const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// Get message type
const getMessageType = (msg) => {
    if (!msg) return "unknown";
    if (msg.conversation) return "text";
    if (msg.extendedTextMessage) return "extended_text";
    if (msg.imageMessage) return "image";
    if (msg.videoMessage) return "video";
    if (msg.audioMessage) return "audio";
    if (msg.voiceMessage) return "voice";
    if (msg.documentMessage) return "document";
    if (msg.stickerMessage) return "sticker";
    if (msg.locationMessage) return "location";
    if (msg.contactMessage) return "contact";
    if (msg.buttonsMessage) return "buttons";
    if (msg.listMessage) return "list";
    if (msg.templateMessage) return "template";
    if (msg.protocolMessage) return "protocol";
    return "unknown";
};

// Extract enhanced media info if present with error safety
const extractMediaInfo = async (msg) => {
    const mediaTypes = [
        { type: "imageMessage", name: "image" },
        { type: "videoMessage", name: "video" },
        { type: "audioMessage", name: "audio" },
        { type: "voiceMessage", name: "voice" },
        { type: "documentMessage", name: "document" },
        { type: "stickerMessage", name: "sticker" },
    ];

    for (const { type, name } of mediaTypes) {
        if (msg[type]) {
            const media = msg[type];

            // Download media to get accessible URL
            // Safely wrapped to prevent crashes
            let downloadedMedia = null;
            try {
                downloadedMedia = await downloadAndSaveMedia(msg);
            } catch (downloadError) {
                console.error('Unexpected error in downloadAndSaveMedia:', downloadError);
                downloadedMedia = {
                    filename: null,
                    filepath: null,
                    accessibleUrl: null,
                    size: null,
                    mimetype: media.mimetype || null,
                    error: `Download error: ${downloadError.message}`,
                    downloadError: true,
                };
            }

            // Handle case where download returned null (no media)
            if (!downloadedMedia) {
                downloadedMedia = {
                    filename: null,
                    filepath: null,
                    accessibleUrl: null,
                    size: null,
                    mimetype: media.mimetype || null,
                    error: 'Failed to download media: unknown error',
                    downloadError: true,
                };
            }

            return {
                mediaType: name,
                mimetype: media.mimetype,
                caption: media.caption || null,
                fileName: media.fileName || null,
                fileSize: media.fileLength || null,
                fileSizeFormatted: formatFileSize(media.fileLength),
                // Original encrypted URL (not directly accessible)
                url: media.url || null,
                // New accessible URL (downloaded and served locally)
                accessibleUrl: downloadedMedia?.accessibleUrl || null,
                // File info
                filename: downloadedMedia?.filename || null,
                downloadedSize: downloadedMedia?.size || null,
                directPath: media.directPath || null,
                mediaKey: media.mediaKey || null,
                height: media.height || null,
                width: media.width || null,
                seconds: media.seconds || null,
                duration: media.seconds ? formatDuration(media.seconds) : null,
                gifPlayback: media.gifPlayback || false,
                thumbnailDirectPath: media.thumbnailDirectPath || null,
                mediaKeyTimestamp: media.mediaKeyTimestamp || null,
                // Size limit information
                fileSizeExceeded: downloadedMedia?.fileSizeExceeded || false,
                fileSizeLimit: downloadedMedia?.fileSizeLimit || MAX_MEDIA_SIZE_BYTES,
                fileSizeLimitFormatted:
                    downloadedMedia?.fileSizeLimitFormatted ||
                    formatFileSize(MAX_MEDIA_SIZE_BYTES),
                // Error information
                downloadError: downloadedMedia?.error || null,
                downloadSkipped: downloadedMedia?.skipped || false,
                downloadTimeout: downloadedMedia?.timeout || false,
                serverBusy: downloadedMedia?.serverBusy || false,
            };
        }
    }
    return null;
};

// Extract enhanced context info (quoted messages, mentions, forwarding)
const extractContextInfo = (msg) => {
    const contextTypes = [
        "extendedTextMessage",
        "imageMessage",
        "videoMessage",
        "documentMessage",
        "audioMessage",
    ];

    for (const type of contextTypes) {
        if (msg[type]?.contextInfo) {
            const ctx = msg[type].contextInfo;
            return {
                quotedMessageId: ctx.stanzaId || null,
                quotedParticipant: ctx.participant || null,
                mentionedJid: ctx.mentionedJid || null,
                quotedMessage: ctx.quotedMessage || null,
                expiration: ctx.expiration || null,
                forwardingScore: ctx.forwardingScore || 0,
                isForwarded: (ctx.forwardingScore || 0) > 0,
                externalAdReply: ctx.externalAdReply || null,
                advertising: ctx.advertising || null,
            };
        }
    }
    return null;
};

/**
 * Build complete enhanced webhook payload for fallback/standard mode
 * @param {Object} params - Parameters for building the payload
 * @param {string} params.sessionId - Session ID
 * @param {string} params.sessionDeviceId - Session device ID
 * @param {Object} params.socket - Baileys socket instance
 * @param {Object} params.message - Message object from Baileys
 * @param {string} params.plainText - Extracted plain text from message
 * @param {string} params.userId - User ID (remote JID)
 * @param {Object} params.sessionData - Session data Map
 * @param {Object} params.menuConfig - Menu configuration
 * @param {string} params.processingMode - Processing mode (e.g., "standard_fallback", "menu_flow", "followup_state")
 * @param {string} params.fallbackReason - Reason for fallback
 * @param {Object} params.userSessionData - User session data
 * @param {Function} params.isSessionExpired - Function to check if session is expired
 * @returns {Promise<Object>} Enhanced webhook payload
 */
export const buildEnhancedWebhookPayload = async ({
    sessionId,
    sessionDeviceId,
    socket,
    message,
    plainText,
    userId,
    sessionData,
    menuConfig = null,
    processingMode = "standard_fallback",
    fallbackReason = "unknown",
    userSessionData = null,
    isSessionExpired = () => false,
}) => {
    // ============================================
    // INPUT VALIDATION - Prevent crashes from bad data
    // ============================================
    if (!message || !message.message) {
        console.error('Invalid message object received');
        throw new Error('Invalid message object: message or message.message is missing');
    }

    if (!sessionId) {
        console.error('Missing sessionId');
        throw new Error('Missing required parameter: sessionId');
    }

    if (!userId) {
        console.error('Missing userId');
        throw new Error('Missing required parameter: userId');
    }

    const messageType = getMessageType(message.message);

    // Build complete enhanced webhook payload
    const webhookPayload = {
        // ============================================
        // SESSION & DEVICE INFORMATION
        // ============================================
        sessionId,
        sessionDeviceId: sessionDeviceId,
        devicePhoneNumber: socket.user?.id?.split(":")[0] || null,
        deviceStatus: sessionData.get(sessionId)?.status || "unknown",

        // ============================================
        // SENDER INFORMATION
        // ============================================
        userId,
        senderNumber: message.key.remoteJidAlt || userId,
        pushName: message.pushName || null,
        senderName: message.pushName || null,
        senderType: userId.includes("@g.us")
            ? "group"
            : userId.includes("@broadcast")
              ? "broadcast"
              : userId.includes("@newsletter")
                ? "newsletter"
                : "individual",

        // ============================================
        // MESSAGE METADATA
        // ============================================
        messageId: message.key.id,
        messageTimestamp: message.messageTimestamp || Date.now(),
        // Safely convert timestamp to ISO string
        messageTimestampISO: safeTimestampToISO(message.messageTimestamp),
        messageType,
        messageFromMe: message.key.fromMe || false,
        messageStatus: "received",

        // ============================================
        // MESSAGE CONTENT
        // ============================================
        messageText: plainText,
        messageContent: {
            text: plainText,
            // Note: media is async and will be populated after this object is created
            media: null,
            context: extractContextInfo(message.message),
        },

        // ============================================
        // ADDRESSING & ROUTING
        // ============================================
        addressingMode: message.key.addressingMode,
        participant: message.key.participant || null,
        participantAlt: message.key.participantAlt || null,
        remoteJid: message.key.remoteJid,

        // ============================================
        // DEVICE & CHAT TYPE INFO
        // ============================================
        deviceType: message.key.id?.split(":")?.[1] || "unknown",
        isGroup: userId.includes("@g.us"),
        isBroadcast: userId.includes("@broadcast"),
        isNewsletter: userId.includes("@newsletter"),
        groupMetadata: userId.includes("@g.us")
            ? {
                  groupId: userId,
                  groupJid: message.key.remoteJid,
                  participantJid: message.key.participant,
              }
            : null,

        // ============================================
        // PROCESSING CONTEXT
        // ============================================
        processingMode,
        processingTimestamp: new Date().toISOString(),
        fallbackReason,
        webhookUrl: null, // Will be set by caller
        webhookAttempt: "fallback",

        // ============================================
        // MENU & SESSION CONTEXT
        // ============================================
        menuEnabled: menuConfig?.is_menu_enabled || false,
        menuConfig: menuConfig
            ? {
                  menuId: menuConfig.id || null,
                  menuName: menuConfig.name || null,
                  isMenuEnabled: menuConfig.is_menu_enabled || false,
                  mainMenuMessage: menuConfig.main_menu_message || null,
              }
            : null,
        matchedMenuItem: null,
        userSession: userSessionData
            ? {
                  state: userSessionData.state || null,
                  webhookUrl: userSessionData.data || null,
                  updatedAt: userSessionData.updated_at || null,
                  isExpired: isSessionExpired(userSessionData.updated_at),
              }
            : null,

        // ============================================
        // ADDITIONAL METADATA
        // ============================================
        serverTimestamp: new Date().toISOString(),
        serverTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        version: "1.0.0",

        // ============================================
        // RAW MESSAGE (for advanced users)
        // Currently disabled - uncomment if needed for debugging
        // ============================================
        // raw: {
        //     key: message.key,
        //     message: message.message,
        //     pushName: message.pushName,
        //     messageTimestamp: message.messageTimestamp,
        // },
    };

    // Download media and populate the accessible URL
    // Wrapped with extensive error handling to prevent crashes
    try {
        const mediaInfo = await extractMediaInfo(message.message);

        // Validate mediaInfo before adding to payload
        if (mediaInfo && typeof mediaInfo === 'object') {
            webhookPayload.messageContent.media = mediaInfo;

            // Log warnings for problematic downloads
            if (mediaInfo.downloadError) {
                console.warn(`Media download failed:`, {
                    type: mediaInfo.mediaType,
                    error: mediaInfo.downloadError,
                    skipped: mediaInfo.downloadSkipped,
                    timeout: mediaInfo.downloadTimeout,
                    serverBusy: mediaInfo.serverBusy,
                });
            }

            if (mediaInfo.fileSizeExceeded) {
                console.warn(`Media file size exceeded:`, {
                    type: mediaInfo.mediaType,
                    size: mediaInfo.fileSize,
                    limit: mediaInfo.fileSizeLimit,
                });
            }
        } else {
            webhookPayload.messageContent.media = null;
        }
    } catch (mediaError) {
        console.error(`Unexpected error extracting media info:`, mediaError);
        // Keep media as null and add error info to help debugging
        webhookPayload.messageContent.media = null;
        webhookPayload.messageContent.mediaError = {
            message: mediaError.message,
            stack: process.env.NODE_ENV === 'development' ? mediaError.stack : undefined,
        };
    }

    return webhookPayload;
};

/**
 * Safe wrapper for buildEnhancedWebhookPayload with additional crash protection
 * This function catches ALL errors and returns a safe error response
 *
 * @param {Object} params - Same parameters as buildEnhancedWebhookPayload
 * @returns {Promise<Object>} Safe webhook payload or error payload
 */
export const safeBuildEnhancedWebhookPayload = async (params) => {
    try {
        // Validate required parameters
        if (!params || typeof params !== 'object') {
            throw new Error('Invalid parameters: params must be an object');
        }

        // Call the main function
        const payload = await buildEnhancedWebhookPayload(params);

        // Validate the returned payload
        if (!payload || typeof payload !== 'object') {
            console.error('buildEnhancedWebhookPayload returned invalid payload');
            return createErrorPayload(params, 'Invalid payload generated');
        }

        return payload;

    } catch (error) {
        console.error('Fatal error in buildEnhancedWebhookPayload:', error);

        // Return a safe error payload instead of crashing
        return createErrorPayload(params, error.message, error);
    }
};

/**
 * Create a safe error payload when webhook generation fails
 */
const createErrorPayload = (params, errorMessage, error = null) => {
    const now = new Date().toISOString();

    return {
        // Basic session info
        sessionId: params?.sessionId || 'unknown',
        sessionDeviceId: params?.sessionDeviceId || 'unknown',

        // Error information
        error: {
            message: errorMessage,
            type: 'webhook_generation_error',
            timestamp: now,
            ...(error && process.env.NODE_ENV === 'development' && {
                stack: error.stack,
            }),
        },

        // Available user info (if available)
        userId: params?.userId || null,
        messageText: params?.plainText || null,

        // Processing context
        processingMode: params?.processingMode || 'error',
        processingTimestamp: now,
        fallbackReason: params?.fallbackReason || 'webhook_generation_failed',

        // Server status
        serverStatus: {
            activeDownloads,
            downloadCapacity: getDownloadCapacity(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
    };
};
